import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  EmployeeImpactPerTypeRow,
  useEmployeeImpactPerType,
  useEmployeeImpactPerTypeExport,
} from "@/pages/dashboard/hooks/useEmployeeImpact";
import { formatAmountWithUnit } from "@/pages/dashboard/utils/formatters";
import { DownloadOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import Widget from "../../components/Widget/Widget";
import { ImpactMetric } from "../../utils/types";

export default function EmployeeImpactPerType({ metric }: { metric: ImpactMetric }) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 10 },
      defaultSort: {
        key: metric.key,
        order: "descend",
      },
    });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);
  const translation = useTranslation();

  const columns: Column<EmployeeImpactPerTypeRow>[] = [
    {
      label: "Travel mode",
      key: "name",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
    },
    {
      label: "Type",
      key: "impact_type",
      maxWidth: "1fr",
      filterable: true,
      renderer: (record) => {
        return record.impact_type === "commute" ? "Commute" : "Business travel";
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record) => {
        return metric.formatter(record[metric.key]);
      },
    },
    {
      label: "Share",
      key: "share",
      maxWidth: "1fr",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record[metric.key]}
            value={data?.company_employee_impact[metric.key]}
          />
        );
      },
    },
    {
      label: "Distance",
      key: "distance",
      renderer: (record) => {
        return `${formatAmountWithUnit(record.distance, "km", 0)}`;
      },
    },
  ];

  const { data, isLoading, isFetching } = useEmployeeImpactPerType({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const exportMutation = useEmployeeImpactPerTypeExport({
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  return (
    <Widget
      title={"Employee impact side-by-side"}
      tooltip={translation["dashboard"]["employee"]["side_by_side"]}
      topRight={
        <>
          <FileDownloader
            fileUrlFn={() => exportMutation.mutateAsync()}
            buttonText={<DownloadOutlined />}
            fileOutputName="employee-impact.xlsx"
            addTimestampAndCompany={true}
          />
        </>
      }
    >
      <Table
        availableFilters={{
          impact_type: [
            { label: "Commute", value: "commute" },
            { label: "Business travel", value: "business" },
          ],
        }}
        hideRaster={true}
        type={"grey"}
        filter={filter}
        sort={sort}
        search={search}
        columns={columns}
        data={data?.employee_impact}
        loading={isLoading || isFetching}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        pagination={pagination}
        totalRows={data?.total_rows}
        rowKey={"key"}
      />
    </Widget>
  );
}
